import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building, 
  Calendar, 
  Link as LinkIcon, 
  MapPin, 
  Phone, 
  CheckSquare, 
  Check, 
  ArrowRight,
  Building2,
  BarChart,
  Award,
  CreditCard
} from 'lucide-react';
import { toast } from '../components/ui/toast/use-toast';
import UpgradeToInmobiliariaLayout from './UpgradeToInmobiliariaLayout';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.03,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.2 }
  }
};

const UpgradeToInmobiliaria = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const totalSteps = 3;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');

  const [formData, setFormData] = useState({
    nombre: user?.nombre || '',       
    nombre_empresa: user?.nombre_empresa || '',   
    slug: user?.slug || '',            
    ruc: user?.ruc || '',            
    servicios: user?.servicios || [],       
    selectedPlan: null,  
    selectedPlanDetails: null,
    billingCycle: 'monthly'
  });

  // Validar tipo de usuario al cargar el componente
  useEffect(() => {
    if (user && user.tipo_usuario_id !== 2) {
      toast({
        title: "Acceso no permitido",
        description: "No tienes permiso para acceder a esta página",
        variant: "destructive"
      });
      navigate('/configuracion');
    }
  }, [user, navigate]);

  // Lista de servicios disponibles para inmobiliarias
  const SERVICIOS_INMOBILIARIOS = [
    'Compra y venta de propiedades',
    'Alquiler de inmuebles',
    'Tasaciones profesionales',
    'Asesoría legal inmobiliaria',
    'Gestión de propiedades',
    'Marketing inmobiliario',
    'Inversiones inmobiliarias',
    'Financiamiento inmobiliario',
    'Administración de propiedades',
    'Servicios postventa'
  ];

  // Planes de inmobiliaria
  const PLANES_INMOBILIARIA = [
    {
        id: 'basico',
        name: 'Básico',
        price: '199.90',
        properties: 10,
        color: 'from-green-500 to-teal-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-green-600 to-teal-600',
        icon: Building2,
        benefits: [
          'Hasta 10 publicaciones',
          'Perfil empresarial',
          'Estadísticas básicas',
          'Soporte por email'
        ]
      },
      {
        id: 'estandar',
        name: 'Estándar',
        price: '349.90',
        properties: 20,
        color: 'from-indigo-500 to-blue-600',
        textColor: 'text-gray-800',
        gradientButton: 'from-indigo-600 to-blue-600',
        icon: BarChart,
        benefits: [
          'Hasta 20 publicaciones',
          'Perfil empresarial destacado',
          'Estadísticas avanzadas',
          'Soporte prioritario',
          'Logo en propiedades'
        ],
        featured: true
      },
      {
        id: 'premium',
        name: 'Premium',
        price: '499.90',
        properties: 'Ilimitadas',
        color: 'from-amber-500 to-yellow-500',
        textColor: 'text-gray-800',
        gradientButton: 'from-amber-600 to-yellow-600',
        icon: Award,
        benefits: [
          'Publicaciones ilimitadas',
          'Perfil empresarial premium',
          'Estadísticas en tiempo real',
          'Soporte 24/7',
          'Logo destacado',
        ]
      }
  ];

  // Calcular precio anual (10 meses) y ahorro (2 meses)
  const calculateAnnualPrice = (monthlyPrice) => {
    if (monthlyPrice === '0') return '0';
    const monthly = parseFloat(monthlyPrice);
    return (monthly * 10).toFixed(2);
  };

  const calculateSavings = (monthlyPrice) => {
    if (monthlyPrice === '0') return '0';
    const monthly = parseFloat(monthlyPrice);
    return (monthly * 2).toFixed(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validaciones específicas
    switch (name) {
      case 'ruc':
        if (value && !/^\d{11}$/.test(value)) {
          setErrors(prev => ({
            ...prev,
            ruc: 'El RUC debe tener 11 dígitos'
          }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.ruc;
            return newErrors;
          });
        }
        break;

      case 'slug':
        // Validar slug (solo letras minúsculas, números y guiones)
        const slugValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setFormData(prev => ({
          ...prev,
          slug: slugValue
        }));
        break;
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      return { ...prev, servicios: newServices };
    });
  };

  const handlePlanSelect = (plan) => {
    setFormData(prev => ({
      ...prev,
      selectedPlan: plan.id,
      selectedPlanDetails: plan,
      billingCycle: billingCycle
    }));
  };

  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1:
        if (!formData.nombre) {
          newErrors.nombre = 'El nombre de la inmobiliaria es requerido';
        }
        if (!formData.nombre_empresa) {
          newErrors.nombre_empresa = 'La razón social es requerida';
        }
        if (!formData.ruc) {
          newErrors.ruc = 'El RUC es requerido';
        } else if (!/^\d{11}$/.test(formData.ruc)) {
          newErrors.ruc = 'El RUC debe tener 11 dígitos';
        }
        if (!formData.slug) {
          newErrors.slug = 'El identificador único es requerido';
        } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
          newErrors.slug = 'El identificador solo puede contener letras minúsculas, números y guiones';
        }
        break;

      case 2:
        if (formData.servicios.length === 0) {
          newErrors.servicios = 'Debe seleccionar al menos un servicio';
        }
        if (formData.servicios.length > 6) {
          newErrors.servicios = 'No puede seleccionar más de 6 servicios';
        }
        break;

      case 3:
        if (!formData.selectedPlan) {
          newErrors.selectedPlan = 'Debe seleccionar un plan';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleFinish = async () => {
    if (!validateStep()) return;
  
    setLoading(true);
    try {
      // Preparar los datos según el formato que espera el endpoint existente
      const requestData = {
        nombre: formData.nombre,
        nombre_empresa: formData.nombre_empresa,
        ruc: formData.ruc,
        slug: formData.slug,
        servicios: formData.servicios,
        selectedPlan: formData.selectedPlan,
        selectedPlanDetails: formData.selectedPlanDetails,
        billingCycle: billingCycle,
        userType: 'inmobiliaria'
      };
  
      console.log('Enviando datos para upgrade a inmobiliaria:', requestData);
  
      const response = await fetch('https://backend-production-1786.up.railway.app/api/user/upgrade-to-inmobiliaria', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData)
      });
  
      // Obtener la respuesta como texto primero para mejor debugging
      const responseText = await response.text();
      console.log('Respuesta raw del servidor:', responseText);
  
      // Intentar parsear como JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parseando JSON:', parseError);
        console.error('Respuesta completa:', responseText);
        throw new Error(`Respuesta inválida del servidor. Código de estado: ${response.status}`);
      }
  
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Error en la actualización');
      }
  
      toast({
        title: "¡Felicidades!",
        description: "Has sido actualizado a Inmobiliaria exitosamente",
        variant: "success"
      });
  
      // Opcional: Cerrar sesión para que se actualice el tipo de usuario
      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 2000);
  
    } catch (error) {
      console.error('Error en actualización:', error);
      
      toast({
        title: "Error",
        description: error.message || "No se pudo completar la actualización",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderCompanyInfo();
      case 2:
        return renderServicesStep();
      case 3:
        return renderPlanConfirmation();
      default:
        return null;
    }
  };

  const renderCompanyInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-2xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700 text-center"
        variants={fadeInUp}
      >
        Información de tu Inmobiliaria
      </motion.h3>

      <motion.div className="space-y-4" variants={fadeInUp}>
        <div className="relative">
          <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <input
            type="text"
            placeholder="Nombre de la Inmobiliaria"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.nombre ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {errors.nombre && (
            <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>
          )}
        </div>

        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <input
            type="text"
            placeholder="Razón Social"
            name="nombre_empresa"
            value={formData.nombre_empresa}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.nombre_empresa ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {errors.nombre_empresa && (
            <p className="text-red-500 text-sm mt-1">{errors.nombre_empresa}</p>
          )}
        </div>

        <div className="relative">
          <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <input
            type="text"
            placeholder="RUC"
            name="ruc"
            value={formData.ruc}
            onChange={handleInputChange}
            maxLength="11"
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.ruc ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {errors.ruc && (
            <p className="text-red-500 text-sm mt-1">{errors.ruc}</p>
          )}
        </div>

        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <input
            type="text"
            placeholder="Identificador único (ejemplo: inmobiliaria-lima)"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.slug ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          <p className="text-sm text-indigo-600 mt-1">
            Este será tu URL: inmobiliaria/{formData.slug}
          </p>
          {errors.slug && (
            <p className="text-red-500 text-sm mt-1">{errors.slug}</p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );

  const renderServicesStep = () => (
    <motion.div
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.div variants={fadeInUp} className="text-center mb-8">
        <h3 className="text-2xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">
          Servicios Ofrecidos
        </h3>
        <p className="text-gray-600">
          Selecciona hasta 6 servicios principales que ofrece tu inmobiliaria
        </p>
      </motion.div>

      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        variants={staggerContainer}
      >
        {SERVICIOS_INMOBILIARIOS.map((servicio) => (
          <motion.label
            key={servicio}
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors
              ${formData.servicios.includes(servicio) 
                ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <input
              type="checkbox"
              checked={formData.servicios.includes(servicio)}
              onChange={() => handleServiceChange(servicio)}
              className="hidden"
            />
            <div className={`flex-shrink-0 p-1 rounded-full mr-3 ${
              formData.servicios.includes(servicio) ? 'bg-indigo-100' : 'bg-gray-100'
            }`}>
              <CheckSquare
                className={`w-5 h-5 ${
                  formData.servicios.includes(servicio) 
                    ? 'text-indigo-600' 
                    : 'text-gray-400'
                }`}
              />
            </div>
            <span className={`${formData.servicios.includes(servicio) ? 'font-medium text-indigo-700' : 'text-gray-700'}`}>
              {servicio}
            </span>
          </motion.label>
        ))}
      </motion.div>
      
      {errors.servicios && (
        <motion.p 
          variants={fadeInUp}
          className="text-red-500 text-sm text-center mt-4 bg-red-50 p-3 rounded-lg"
        >
          {errors.servicios}
        </motion.p>
      )}
    </motion.div>
  );

  const renderPlanConfirmation = () => (
    <motion.div 
      className="space-y-8"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.div variants={fadeInUp} className="text-center mb-8">
        <h3 className="text-2xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">
          Elige tu Plan de Inmobiliaria
        </h3>
        <p className="text-gray-600">
          Selecciona el plan que mejor se adapte a las necesidades de tu inmobiliaria
        </p>
      </motion.div>

      {/* Billing cycle toggle */}
      <motion.div variants={fadeInUp} className="flex justify-center items-center mb-8">
        <div className="bg-white p-2 rounded-xl shadow-md">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setBillingCycle('monthly');
                setFormData(prev => ({...prev, billingCycle: 'monthly'}));
              }}
              className={`px-6 py-2 rounded-lg transition-all duration-300 ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => {
                setBillingCycle('annual');
                setFormData(prev => ({...prev, billingCycle: 'annual'}));
              }}
              className={`px-6 py-2 rounded-lg transition-all duration-300 ${
                billingCycle === 'annual'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Anual (2 meses gratis)
            </button>
          </div>
        </div>
      </motion.div>
  
      <motion.div 
        className="grid md:grid-cols-3 gap-8"
        variants={staggerContainer}
      >
        {PLANES_INMOBILIARIA.map((plan) => {
          const Icon = plan.icon;
          const monthlyPrice = plan.price;
          const annualPrice = calculateAnnualPrice(monthlyPrice);
          const savingsAmount = calculateSavings(monthlyPrice);
          
          return (
            <motion.div 
              key={plan.id}
              variants={cardAnimation}
              whileHover="hover"
              onClick={() => handlePlanSelect(plan)}
              className={`relative bg-white rounded-2xl shadow-xl overflow-hidden 
                transition-all duration-300 cursor-pointer
                ${formData.selectedPlan === plan.id ? 'border-2 border-indigo-500 scale-105' : ''}
                ${plan.featured ? 'lg:-mt-4 lg:mb-4' : ''}
              `}
            >
              <div className="absolute top-4 right-4 z-10 flex space-x-2">
                {formData.selectedPlan === plan.id && (
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    Seleccionado
                  </span>
                )}
                {plan.featured && (
                  <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    Recomendado
                  </span>
                )}
              </div>

              <div 
                className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-10`}
              />

              <div className="relative p-8 z-10">
                <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-full bg-gradient-to-br ${plan.color} text-white mr-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={`text-2xl font-bold ${plan.textColor}`}>
                    {plan.name}
                  </h3>
                </div>
                
                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-bold text-gray-800">
                    S/ {billingCycle === 'monthly' ? monthlyPrice : annualPrice}
                  </span>
                  <span className="text-gray-500 ml-2">
                    /{billingCycle === 'monthly' ? 'mes' : 'año'}
                  </span>
                </div>
                
                {billingCycle === 'annual' && monthlyPrice !== '0' && (
                  <div className="mb-6 text-sm text-green-600 font-medium">
                    Ahorras S/ {savingsAmount} al año
                  </div>
                )}
                {billingCycle === 'monthly' && (
                  <div className="mb-6 h-5"></div> // Espacio para mantener alineación
                )}

                <div className="border-t border-gray-100 pt-6 mb-8">
                  <p className="text-sm text-gray-600 mb-4">
                    {typeof plan.properties === 'number' 
                      ? `Hasta ${plan.properties} publicaciones`
                      : 'Publicaciones ilimitadas'}
                  </p>

                  <ul className="space-y-4">
                    {plan.benefits.map((benefit, index) => (
                      <li 
                        key={index} 
                        className="flex items-start gap-3 text-gray-700"
                      >
                        <div className="flex-shrink-0 rounded-full p-1 bg-green-100">
                          <Check className="w-4 h-4 text-green-500" />
                        </div>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <motion.button
                  onClick={() => handlePlanSelect(plan)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`w-full py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-md
                    ${formData.selectedPlan === plan.id
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${plan.gradientButton} text-white hover:shadow-lg`
                    } transition-all duration-300`}
                >
                  {formData.selectedPlan === plan.id ? (
                    'Plan Seleccionado'
                  ) : (
                    <>
                      Seleccionar Plan
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
  
      {errors.selectedPlan && (
        <motion.p
          variants={fadeInUp}
          className="text-red-500 text-sm text-center mt-4 bg-red-50 p-3 rounded-lg"
        >
          {errors.selectedPlan}
        </motion.p>
      )}
    </motion.div>
  );
  
  return (
    <UpgradeToInmobiliariaLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      handleNext={handleNext}
      handleBack={handleBack}
      handleFinish={handleFinish}
      loading={loading}
      title="Conviértete en Inmobiliaria"
      errors={errors}
    >
      {renderStep()}
    </UpgradeToInmobiliariaLayout>
  );
};

export default UpgradeToInmobiliaria;